// @core/stores/subscription/SubscriptionDiscountStore.ts
// Auto-generated by corebuilder. DO NOT EDIT.

import { makeAutoObservable, runInAction } from 'mobx';
import { subscriptionApi } from '@core/api';
import type {
  CreateDiscountRequest,
  Discount,
  UpdateDiscountRequest,
  ValidateDiscountRequest,
  ValidateDiscountResponse,
} from '@core/types/subscription';
import type { PaginatedRequest, PageInfo } from '@core/types/pagination';

class SubscriptionDiscountStore {
  // List state
  items: Discount[] = [];
  pageInfo: PageInfo | null = null;

  // Single item state
  currentItem: Discount | null = null;

  // Loading states
  isLoading = false;
  isLoadingItem = false;
  isSaving = false;
  isDeleting = false;

  // Error state (stores full ApiClientError for request context)
  error: unknown = null;
  // Cache by ID
  private cache = new Map<string, Discount>();

  constructor() {
    makeAutoObservable(this);
  }
  // Computed
  get hasMore(): boolean {
    return this.pageInfo?.has_next ?? false;
  }

  get isEmpty(): boolean {
    return this.items.length === 0 && !this.isLoading;
  }

  // Cache helpers
  getById(id: string): Discount | undefined {
    return this.cache.get(id);
  }
  private updateCache(item: Discount | undefined): void {
    if (item?.entity?.id) {
      this.cache.set(item.entity.id, item);
    }
  }
  private removeFromCache(id: string): void {
    this.cache.delete(id);
  }

  // Actions
  setError(error: unknown): void {
    this.error = error;
  }

  clearError(): void {
    this.error = null;
  }
  clearCurrent(): void {
    this.currentItem = null;
  }

  reset(): void {
    this.items = [];
    this.pageInfo = null;
    this.currentItem = null;
    this.error = null;
    this.cache.clear();
  }

  /**
   * Create a discount code
   */
  async CreateDiscount(data: CreateDiscountRequest): Promise<Discount> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await subscriptionApi.CreateDiscount(data);
      const item = response.discount;

      runInAction(() => {
        if (item) {
          this.items = [item, ...this.items];
          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Delete discount
   */
  async DeleteDiscount(discount_id: string): Promise<void> {
    this.isDeleting = true;
    this.error = null;

    try {
      await subscriptionApi.DeleteDiscount(discount_id);

      runInAction(() => {
        // Remove from list
        this.items = this.items.filter(i => i.entity?.id !== discount_id);

        // Clear current item if same
        if (this.currentItem?.entity?.id === discount_id) {
          this.currentItem = null;
        }

        this.removeFromCache(discount_id);
        this.isDeleting = false;
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isDeleting = false;
      });
      throw error;
    }
  }

  /**
   * Get discount details
   */
  async GetDiscount(discount_id: string): Promise<Discount> {
    // Check cache first
    const cached = this.cache.get(discount_id);
    if (cached) {
      this.currentItem = cached;
      return cached;
    }

    this.isLoadingItem = true;
    this.error = null;

    try {
      const response = await subscriptionApi.GetDiscount(discount_id);
      const item = response.discount;

      runInAction(() => {
        this.currentItem = item ?? null;
        this.updateCache(item);
        this.isLoadingItem = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoadingItem = false;
      });
      throw error;
    }
  }

  /**
   * List discounts
   */
  async ListDiscounts(params?: PaginatedRequest): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      const response = await subscriptionApi.ListDiscounts(params);

      runInAction(() => {
        this.items = response.discounts?.items ?? [];
        this.pageInfo = response.pagination?.page_info ?? null;
        this.isLoading = false;

        // Update cache
        this.items.forEach(item => this.updateCache(item));
      });
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }

  /**
   * Update discount
   */
  async UpdateDiscount(discount_id: string, data: UpdateDiscountRequest): Promise<Discount> {
    this.isSaving = true;
    this.error = null;

    try {
      const response = await subscriptionApi.UpdateDiscount(discount_id, data);
      const item = response.discount;

      runInAction(() => {
        if (item) {
          // Update in list
          const index = this.items.findIndex(i => i.entity?.id === item.entity?.id);
          if (index !== -1) {
            this.items[index] = item;
          }

          // Update current item if same
          if (this.currentItem?.entity?.id === item.entity?.id) {
            this.currentItem = item;
          }

          this.updateCache(item);
        }
        this.isSaving = false;
      });

      return item!;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isSaving = false;
      });
      throw error;
    }
  }

  /**
   * Validate discount code
   */
  async ValidateDiscount(data: ValidateDiscountRequest): Promise<ValidateDiscountResponse> {
    this.isLoading = true;
    this.error = null;

    try {
      const result = await subscriptionApi.ValidateDiscount(data);

      runInAction(() => {
        this.isLoading = false;
      });

      return result;
    } catch (error) {
      runInAction(() => {
        this.error = error;
        this.isLoading = false;
      });
      throw error;
    }
  }
}

export const subscriptionDiscountStore = new SubscriptionDiscountStore();
